import { OdlThemeType } from "@odl/core";
import { Components, ThemeOptions } from "@mui/material";

export const computeChipColor = (theme: any, ownerState: any) => {
  const color = theme.palette?.[ownerState.color];
  return color;
};

const themeComponentOverride = (theme: OdlThemeType & ThemeOptions): Components => ({
  MuiFilledInput: {
    styleOverrides: {
      root: {
        borderBottomColor: theme.palette?.grey?.["600"],
        background: theme.palette?.background?.papershade,

        "&.Mui-readOnly": {
          borderBottomColor: "transparent",
        },

        "&:hover, &:active, &:focus": {
          background: theme.palette?.grey?.["400"],
        },
      },
    },
  },

  MuiList: {
    styleOverrides: {
      root: {
        "&": {
          maxHeight: "250px ",
        },
      },
    },
  },

  MuiMenuItem: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        "&:hover": {
          background: theme.palette?.grey?.["400"],
        },
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      root: {
        "& .MuiPaper-root": {
          boxShadow:
            "0px 9px 46px 8px rgba(0, 0, 0, 0.12),0px 24px 38px 3px rgba(0, 0, 0, 0.14),0px 11px 15px -7px rgba(0, 0, 0, 0.2)",
        },
      },
    },
  },

  MuiFormControl: {
    styleOverrides: {
      root: {
        textarea: {
          background: theme.palette?.background?.papershade,
          lineHeight: "20px",
          "&:hover, &:active, &:focus": {
            background: theme.palette?.grey?.["400"],
          },
        },

        ".MuiFilledInput-root.Mui-focused": {
          background: theme.palette?.grey?.["400"],
        },

        ".MuiFormLabel-root": {
          margin: "0 0 10px !important",
          fontSize: "14px",
          fontWeight: 600,
          color: theme.palette?.text?.primary,
        },
      },
    },
  },

  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: theme.palette?.text?.primary,
        margin: "0",
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
      disableInteractive: true,
    },
    styleOverrides: {
      tooltip: {
        maxWidth: "50vw",
        table: { display: "none" },
        img: { display: "none" },
        a: { color: (theme.palette?.primary as { main?: string })?.main },
      },
      popper: {
        zIndex: 2001,
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: {
        ".MuiAutocomplete-endAdornment": {
          top: "22px !important",
        },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: "44px",
        paddingBottom: "0 !important",
      },
    },
  },

  MuiTab: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        fontWeight: 600,
        fontSize: "16px",
        padding: `14px 12px 10px`,

        textTransform: "none",
        minWidth: "inherit",
        minHeight: "inherit",

        color: theme.palette?.text?.secondary,
        opacity: 1,
        ...theme.mixins?.transition!(),

        border: `solid 2px transparent !important`,

        "&:hover": {
          backgroundColor: theme.palette?.grey?.[400],
        },

        "&:focus-visible": {
          outline: "none",
          borderColor: `${(theme.palette?.blue as { main?: string })?.main} !important`,
        },
      },
    },
  },

  MuiSwitch: {
    defaultProps: {
      disableRipple: true,
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {},
    },
  },

  MuiTableCell: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {},
  },

  MuiPopover: {
    defaultProps: {
      elevation: 2,
    },
    styleOverrides: {
      root: {
        zIndex: 2000,
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        "&:hover": {
          backgroundColor: `${theme.palette?.grey?.[400]} !important`,
        },
        "& .i-navigation-chevron-down:before": {
          color: theme.palette?.text?.secondary,
        },
      },
    },
  },

  MuiAvatarGroup: {
    styleOverrides: {
      root: {
        "& .MuiAvatar-root": {
          borderColor: theme.palette?.background?.paper,
          borderSize: "1px",
        },
      },
    },
  },

  MuiChip: {
    defaultProps: {
      variant: "filled",
      color: "primary",
    },
    styleOverrides: {
      root: ({ ownerState, theme: Theme }) => ({
        ...theme.mixins?.transition!(),
        borderRadius: "4px",
        lineHeight: "auto",
        border: "1px solid transparent",

        ...(ownerState.clickable && {
          borderColor: "transparent",
        }),

        ...(ownerState.variant === "outlined" && {
          color: computeChipColor(theme, ownerState)?.contrastText,
          backgroundColor: "transparent",
          borderColor: computeChipColor(theme, ownerState)?.contrastText,
        }),

        ...(ownerState.variant === "filled" && {
          backgroundColor: computeChipColor(theme, ownerState)?.main,
          color: computeChipColor(theme, ownerState)?.contrastText,

          ...(ownerState.clickable && {
            "&:hover": {
              backgroundColor: computeChipColor(theme, ownerState)?.main,
              borderColor: computeChipColor(theme, ownerState)?.contrastText,
            },
          }),
        }),

        "& .MuiChip-icon": {
          height: "auto",
        },

        "& .MuiChip-deleteIcon": {
          margin: "0 0 0 4px",
          fontSize: "20px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: computeChipColor(theme, ownerState)?.contrastText,
        },
      }),
      sizeSmall: {
        height: "24px",
        fontSize: "12px",
        fontWeight: 500,
      },
    },
  },

  MuiToggleButton: {
    defaultProps: {
      color: "primary",
      size: "small",
    },
    styleOverrides: {
      root: ({ ownerState, theme: Theme }) => ({
        fontSize: "14px",
        color: theme.palette?.text?.secondary,

        "&.Mui-selected": {
          backgroundColor: computeChipColor(theme, ownerState)?.main,
          color: computeChipColor(theme, ownerState)?.contrastText,
          borderColor: computeChipColor(theme, ownerState)?.contrastText,
        },
      }),
    },
  },

  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      size: "small",
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        border: `1px solid ${(theme.palette?.blue as { main?: string })?.main}`,
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: theme.palette?.grey?.["300"],
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        height: "78px",
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        paddingTop: 0,
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        justifyContent: "space-between",
        backgroundColor: theme.palette?.background?.default,
      },
    },
  },

  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        ".MuiFormControlLabel-label": {
          fontWeight: 500,
          color: theme.palette?.text?.primary,
        },

        "& .MuiSwitch-root": {
          padding: 0,
          height: "inherit",
          width: "inherit",
        },

        "& .MuiSwitch-switchBase": {
          height: "100%",
          padding: 0,
          transform: "translateX(6px)",
          "&.Mui-checked": {
            transform: "translateX(30px)",
          },
        },
        "& .MuiSwitch-track": {
          height: 20,
          borderRadius: 20,
          opacity: 1,
          width: 44,
          backgroundColor: theme.palette?.grey?.[800],
          border: `4px solid ${theme.palette?.background?.paper}`,

          "&::before, &::after": {
            content: '""',
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            width: 16,
            height: 16,
          },
          "&::after": {
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24"><path fill="white" d="M12.6667 4.27331L11.7267 3.33331L8.00004 7.05998L4.27337 3.33331L3.33337 4.27331L7.06004 7.99998L3.33337 11.7266L4.27337 12.6666L8.00004 8.93998L11.7267 12.6666L12.6667 11.7266L8.94004 7.99998L12.6667 4.27331Z" /></svg>')`,
            right: 8,
          },
        },

        "& .Mui-checked+.MuiSwitch-track": {
          "&::before": {
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24"><path fill="white" d="M13.4 5.06823L6.06669 12.4016L2.40002 8.7349L3.34002 7.7949L6.06669 10.5149L12.46 4.12823L13.4 5.06823Z"/></svg>')`,
            left: 9,
          },

          backgroundColor: (theme.palette?.blue as { main?: string })?.main,
          opacity: "1 !important",
        },
        "& .MuiSwitch-thumb": {
          width: 16,
          height: 16,
          backgroundColor: theme.palette?.background?.papershade,
          boxShadow: "0px 1px 5px 0px #00000033,  0px 3px 1px -2px #0000001F, 0px 2px 2px 0px #00000024",
        },

        "&:hover .Mui-checked+.MuiSwitch-track": {
          backgroundColor: (theme.palette?.blue as { main?: string })?.main,
          borderColor: theme.palette?.background?.paper,
        },
        "&:hover .MuiSwitch-track": {
          borderColor: theme.palette?.grey?.[400],
        },
      },
    },
  },

  MuiIconButton: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        color: theme.palette?.text?.secondary,
        cursor: "pointer",

        "&.Mui-disabled": {
          cursor: "not-allowed",
          color: theme.palette?.text?.disabled,
          pointerEvents: "auto",
        },

        "&:hover:not(.Mui-disabled)": {
          backgroundColor: theme.palette?.grey?.[300],
          color: (theme.palette?.primary as { main?: string })?.main,
        },
      },
    },
  },

  MuiListItem: {
    styleOverrides: {
      root: {
        boxSizing: "border-box",
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        "&:focus-visible": {
          outline: `2px solid ${(theme.palette?.blue as { main?: string })?.main}`,
        },
      },
    },
  },

  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        "$:hover": {
          background: "transparent",
        },

        "&:focus  svg": {
          opacity: 0.5,
        },
      },
      icon: {
        color: theme.palette?.text?.secondary,
        fontSize: "14px",
      },
    },
  },

  MuiTypography: {
    defaultProps: {
      variant: "body2",
    },
    styleOverrides: {
      root: { color: theme.palette?.text?.primary },
      body1: {
        color: theme.palette?.text?.primary,
      },
      h1: {
        color: theme.palette?.text?.primary,
      },
      h2: {
        color: theme.palette?.text?.primary,
      },
      h3: {
        color: theme.palette?.text?.primary,
      },
      h4: {
        color: theme.palette?.text?.primary,
      },
      h5: {
        color: theme.palette?.text?.primary,
      },
      h6: {
        color: theme.palette?.text?.primary,
      },
    },
  },
});

export default themeComponentOverride;
